import { useDispatch, useSelector } from 'react-redux';
import { selectAccessToken, selectSalesSteeringRemarks } from '../../../store/selectors';
import { useEffect } from 'react';
import { getSalesSteeringRemarks } from '../../../store/actions';
import { formatDate, makeDate } from '../../../utilities/formatting';
import { ProgressSpinner } from 'primereact/progressspinner';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import styles from './sales-steering-remarks-list.module.scss';
import { ISalesSteeringRemark } from '../../../store/actions/sales-configuration';
import { isAttentionNeededBodyTemplate } from '../../shared/body-templates';

const highlightAttentionNeeded = (rowData: ISalesSteeringRemark) => {
  return rowData.isAttentionNeeded ? styles.attentionRow : '';
};

interface ISalesSteeringRemarkListProperties {
  salesSteeringId: number;
  percentage: number;
  duration: number;
}

function SalesSteeringRemarkList({ salesSteeringId, percentage, duration }: ISalesSteeringRemarkListProperties) {
  const dispatch = useDispatch();
  const salesSteeringRemarks = useSelector(selectSalesSteeringRemarks)
    .filter((ssr) => ssr.salesSteeringId === salesSteeringId)
    .map((salesSteeringRemark) => ({
      ...salesSteeringRemark,
      startDate: makeDate(salesSteeringRemark.startDate),
      endDate: makeDate(salesSteeringRemark.endDate),
    }));
  const accessToken = useSelector(selectAccessToken);

  useEffect(() => {
    if (accessToken && !salesSteeringRemarks.some((ssr) => ssr.salesSteeringId === salesSteeringId)) {
      dispatch(getSalesSteeringRemarks(salesSteeringId, percentage, duration));
    }
  }, [accessToken]);

  return (
    <>
      {salesSteeringRemarks.length === 0 ? (
        <ProgressSpinner />
      ) : (
        <div className={styles.subTable}>
          <DataTable
            sortField="startDate"
            sortOrder={1}
            value={salesSteeringRemarks}
            rowClassName={highlightAttentionNeeded}
          >
            <Column
              field="startDate"
              sortable
              dataType="date"
              header={
                <>
                  Start
                  <br />
                  Date
                </>
              }
              body={(entry) => formatDate(entry.startDate)}
            ></Column>
            <Column
              field="endDate"
              sortable
              dataType="date"
              header={
                <>
                  End
                  <br />
                  Date
                </>
              }
              body={(entry) => formatDate(entry.endDate)}
            ></Column>
            <Column field="remark" sortable header="Remark"></Column>
            <Column
              field="entityType"
              sortable
              header={
                <>
                  Entity
                  <br />
                  Type
                </>
              }
            ></Column>
            <Column
              field="entityValue"
              sortable
              header={
                <>
                  Entity
                  <br />
                  Value
                </>
              }
            ></Column>
            <Column field="type" sortable header="Type"></Column>
            <Column
              field="isAttentionNeeded"
              sortable
              dataType="boolean"
              header={<i className="pi pi-exclamation-triangle" />}
              body={isAttentionNeededBodyTemplate}
            ></Column>
          </DataTable>
        </div>
      )}
    </>
  );
}

export default SalesSteeringRemarkList;
